import React, { useContext } from 'react';
import {
  Flex,
  styled,
  H1,
  H3,
  Text,
  FlexItem,
  FormContextType,
  FormContext,
  Button,
  Icon,
  Input,
} from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';

import { AddNewShiftInitialData } from '../../AddNewShiftWizard';
import { DatesSelectionInfo } from '../SelectShiftsStep/components/DatesSelectionInfo';
import { WizardContext, AddNewShiftWizardFields } from '../../types';
import {
  asFormItem,
  SelectDatesForm,
} from '../SelectShiftsStep/SelectShiftsStep';

const { FormattedMessage, injectIntl } = Intl;

const Title = styled(H1)({
  marginBottom: 0,
});

const OffsetFlex = styled(Flex)({
  paddingLeft: 100,
  paddingRight: 80,
  maxHeight: '60vh',
});

const CustomButton = styled(Button)(({ theme }) => ({
  border: '1px dashed',
  borderColor: theme.newColors.linkPrimary,
  background: 'transparent',
  color: theme.newColors.linkPrimary,
  '&:hover': {
    background: 'transparent',
    color: theme.newColors.linkPrimaryHover,
    borderColor: theme.newColors.linkPrimaryHover,
  },
  '&:focus': {
    background: 'transparent',
    color: theme.newColors.linkPrimaryHover,
    borderColor: theme.newColors.linkPrimaryHover,
  },
}));

const messages = messageIds.vms.newShiftWizard.general;

export const ReviewStep = injectIntl(
  ({
    intl,
    formData,
    pushPrevious,
  }: Intl.InjectedIntlProps & {
    formData: AddNewShiftInitialData;
    pushPrevious: () => void;
  }) => {
    const { departments, shiftTypes, selectedPosition } = formData;

    const formFieldsValues = useContext(WizardContext);

    const form = useContext<FormContextType<AddNewShiftWizardFields>>(
      FormContext,
    );

    const formFields = form.getFieldsValue();

    const onDateQuantityChange = (key: string, quantity: number) => {
      const newSelectedDaysQuantities = {
        ...formFields.selectedDatesQuantities,
      };
      newSelectedDaysQuantities[key] = quantity;

      form.setFieldsValue({
        selectedDatesQuantities: newSelectedDaysQuantities,
      });
    };

    const onDateRemove = (key: string) => {
      const newSelectedDatesQuantities = {
        ...formFields.selectedDatesQuantities,
      };
      const newSelectedDates = { ...formFields.selectedDates };
      const newSelectionPerType = { ...formFields.selectionPerType };

      delete newSelectedDatesQuantities[key];
      delete newSelectedDates[key];

      const allKeys = Object.keys(newSelectedDates);

      newSelectionPerType['range'] = allKeys.filter(
        key => key.indexOf('-') !== -1,
      );
      newSelectionPerType['multiple'] = allKeys.filter(
        key => key.indexOf('-') === -1,
      );

      form.setFieldsValue({
        selectedDates: newSelectedDates,
        selectedDatesQuantities: newSelectedDatesQuantities,
        selectionPerType: newSelectionPerType,
      });
    };

    const selectedDepartment = departments.find(
      department => department.id === formFieldsValues.departmentId,
    );

    const selectedShiftType = shiftTypes.find(
      shiftType => shiftType.id === formFieldsValues.shiftTypeId,
    );

    const reviewItems = [
      {
        label: intl.formatMessage({ id: messages.positionTitle }),
        value: `${selectedPosition?.name} (${selectedPosition?.id})`,
      },
      {
        label: intl.formatMessage({ id: messages.department }),
        value: selectedDepartment?.name,
      },
      {
        label: intl.formatMessage({ id: messages.shiftType }),
        value: selectedShiftType?.name,
      },
      {
        label: intl.formatMessage({ id: messages.startTime }),
        value: formFieldsValues.startTime
          ? formFieldsValues.startTime.format('hh:mm A')
          : '',
      },
      {
        label: intl.formatMessage({ id: messages.endTime }),
        value: formFieldsValues.endTime
          ? formFieldsValues.endTime.format('hh:mm A')
          : '',
      },
    ];

    return (
      <OffsetFlex
        padding={[8, 12, 0, 12]}
        gutter={8}
        gutterDirection="vertical"
        direction="column"
      >
        <SelectDatesForm bottom={120} />
        <div style={{ visibility: 'hidden', position: 'absolute' }}>
          {asFormItem(
            'selectedDatesQuantities',
            messageIds.vms.newShiftWizard.title,
            {},
          )(<Input />)}
          {asFormItem(
            'selectionPerType',
            messageIds.vms.newShiftWizard.title,
            {},
          )(<Input />)}
        </div>
        <FlexItem flex={0} gutter={2} gutterDirection="vertical">
          <Title>
            <FormattedMessage id={messageIds.vms.newShiftWizard.review.title} />
          </Title>
          <Text>
            <FormattedMessage
              id={messageIds.vms.newShiftWizard.review.disclaimer}
            />
          </Text>
        </FlexItem>
        <FlexItem>
          <H3 padding={[0, 0, 4, 0]}>
            <FormattedMessage
              id={messageIds.vms.newShiftWizard.review.subtitle}
            />
          </H3>
          <Flex direction="column" gutter={2} gutterDirection="vertical">
            {reviewItems.map(({ label, value }) => (
              <FlexItem key={label}>
                <Flex>
                  <FlexItem basis="60%">
                    <H3 weight={500} uppercase>
                      {label}
                    </H3>
                  </FlexItem>
                  <FlexItem basis="40%">
                    <H3 weight={400}>{value}</H3>
                  </FlexItem>
                </Flex>
              </FlexItem>
            ))}
          </Flex>
        </FlexItem>
        <FlexItem style={{ overflowY: 'auto' }}>
          <DatesSelectionInfo
            selectedDates={formFields.selectedDates}
            selectedDatesQuantities={formFields.selectedDatesQuantities}
            onDateQuantityChange={onDateQuantityChange}
            onDateRemove={onDateRemove}
          />
        </FlexItem>
        <FlexItem flex={0}>
          <CustomButton onClick={pushPrevious} size="large" block>
            <FormattedMessage
              id={messageIds.vms.newShiftWizard.review.addDate}
            />
            <Icon type="plus" textual />
          </CustomButton>
        </FlexItem>
      </OffsetFlex>
    );
  },
);
